import React, { useEffect, useMemo, useState } from 'react';
import { MjmlDomRender } from '../EditEmailPreview/components/MjmlDomRender';
import { useDropBlock } from '@/hooks/useDropBlock';
import { useHotKeys } from '@/hooks/useHotKeys';
import { SyncScrollShadowDom } from '@/components/UI/SyncScrollShadowDom';
import { ShadowStyle } from './components/ShadowStyle';
import { useEditorContext } from '@/hooks/useEditorContext';
import {
	DATA_ATTRIBUTE_DROP_CONTAINER,
	SYNC_SCROLL_ELEMENT_CLASS_NAME,
} from '@/constants';
import { classnames } from '@/utils/classnames';
import { ActiveTabKeys } from '@/components/Provider/BlocksProvider';
import { useActiveTab } from '@/hooks/useActiveTab';
import { useEditorProps } from '@/hooks/useEditorProps';

// @ts-expect-error - Import from extensions (source only, not for lib build)
import { SubjectPreviewPanel } from '@bay-email/extensions';

export interface EditEmailPreviewProps {
	onSubjectPreviewSave?: (subject: string, previewText: string) => void;
	openFromModal?: boolean;
}

export function EditEmailPreview({
	onSubjectPreviewSave,
	openFromModal = false,
}: EditEmailPreviewProps) {
	useHotKeys();
	const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
	const { setRef } = useDropBlock();
	const { activeTab } = useActiveTab();
	// Block editor variant has no subject/preview — it's a fragment of an email,
	// not a whole email.
	const { blockMode } = useEditorProps();

	const { setInitialized } = useEditorContext();

	useEffect(() => {
		setRef(containerRef);
	}, [containerRef, setRef]);

	useEffect(() => {
		if (containerRef) {
			setInitialized(true);
		}
	}, [containerRef, setInitialized]);

	return useMemo(
		() => (
			<div
				style={{
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
				}}
			>
				{/* Subject Preview Panel - Fixed at top (hidden in block editor variant) */}
				{!blockMode && (
					<div
						id='subject-preview-panel-container'
						style={{
							position: 'relative',
							zIndex: 150,
							borderBottom: '1px solid #e5e5e5',
							backgroundColor: '#fff',
						}}
					>
						<SubjectPreviewPanel
							onSubjectPreviewSave={onSubjectPreviewSave}
							openFromModal={openFromModal}
						/>
					</div>
				)}

				{/* Rich Text Toolbar - Slides down from behind Subject toolbar */}
				<div
					id='rich-text-toolbar-placeholder'
					style={{
						position: 'absolute',
						top: '0',
						left: '0',
						right: '0',
						height: '40px',
						zIndex: 140,
						borderBottom: '1px solid #e5e5e5',
						backgroundColor: '#41444d',
						transform: 'translateY(0)', // Start hidden behind subject toolbar
						transition: 'transform 0.2s ease-in-out, opacity 0.2s ease-in-out',
						pointerEvents: 'none', // Don't block clicks when hidden
						opacity: 0, // Also fade in
					}}
				/>

				{/* Scrollable Email Content */}
				<div style={{ flex: 1, overflow: 'auto', position: 'relative' }}>
					<SyncScrollShadowDom
						isActive={activeTab === ActiveTabKeys.EDIT}
						id='VisualEditorEditMode'
						{...{
							[DATA_ATTRIBUTE_DROP_CONTAINER]: 'true',
						}}
						style={{
							height: '100%',
							zIndex: 10,
							position: 'relative',
							outline: 'none',
						}}
					>
						<div
							id='bay-email-plugins'
							style={{
								position: 'relative',
							}}
						/>
						<div
							className={classnames('shadow-container', SYNC_SCROLL_ELEMENT_CLASS_NAME)}
							style={{
								height: '100%',
								zIndex: 10,
								paddingLeft: 10,
								paddingRight: 10,
								// Reserved for what gets drawn OVER the top of the canvas: the
								// 40px rich-text toolbar that slides in on a text selection,
								// plus the selected block's own control bar, which renders
								// above the block. Block mode used to reserve 20px — enough
								// for neither, so selecting text in the first block put the
								// toolbar on top of it and the block became unreachable.
								paddingTop: 65,
								paddingBottom: 40,
								boxSizing: 'border-box',
							}}
							ref={setContainerRef}
						>
							<MjmlDomRender />
						</div>
						<ShadowStyle />
					</SyncScrollShadowDom>
				</div>
			</div>
		),
		[activeTab, blockMode, onSubjectPreviewSave, openFromModal],
	);
}
